// Utility to handle environment variables across different environments
// For Jest tests, this will use process.env
// For Vite builds, Vite will replace `process.env.*` variables with static values.
//
// Peace Observatory API: VITE_API_BASE_URL is the API origin (scheme + host + port, no path).
// Admin routes live under VITE_ADMIN_API_PREFIX (default /admin). Public PDFs: GET /public/pdfs/{slug}.

function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/$/, '');
}

function normalizeAdminPrefix(prefix: string): string {
  let p = prefix.trim();
  if (!p.startsWith('/')) p = `/${p}`;
  return p.replace(/\/$/, '') || '/admin';
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

export function apiBaseUrl(): string {
  return normalizeBaseUrl(process.env.VITE_API_BASE_URL || 'http://localhost:8000');
}

export function apiUrl(path: string): string {
  return `${apiBaseUrl()}${normalizePath(path)}`;
}

/** Admin route path, e.g. adminPath('/auth/login') -> '/admin/auth/login' */
export function adminPath(path: string): string {
  const prefix = normalizeAdminPrefix(
    process.env.VITE_ADMIN_API_PREFIX || '/admin'
  );
  const segment = normalizePath(path);
  return `${prefix}${segment}`;
}

export function adminApiUrl(path: string): string {
  return apiUrl(adminPath(path));
}

/** API origin for public routes (e.g. /public/pdfs/...). Uses VITE_PUBLIC_API_ORIGIN if set. */
export function apiOrigin(): string {
  const explicit = process.env.VITE_PUBLIC_API_ORIGIN?.trim();
  if (explicit) {
    try {
      return new URL(explicit).origin;
    } catch {
      return explicit.replace(/\/$/, '');
    }
  }
  const base = apiBaseUrl();
  try {
    return new URL(base).origin;
  } catch {
    return normalizeBaseUrl(base);
  }
}

export function publicPdfUrl(publicSlug: string): string {
  const origin = apiOrigin();
  const enc = encodeURIComponent(publicSlug);
  return `${origin}/public/pdfs/${enc}`;
}

// Common environment variable getters
export const ENV = {
  get API_BASE_URL() {
    return apiBaseUrl();
  },

  get ADMIN_API_PREFIX() {
    return normalizeAdminPrefix(process.env.VITE_ADMIN_API_PREFIX || '/admin');
  },

  get MOCK_API_BASE_URL() {
    return (
      process.env.VITE_MOCK_API_BASE_URL ||
      process.env.VITE_MOCK_BASE_URL ||
      'http://localhost:3000'
    );
  },

  get NODE_ENV() {
    return process.env.NODE_ENV || 'development';
  },

  get MODE() {
    // In Vite, use MODE, in Jest use NODE_ENV
    return process.env.MODE || process.env.NODE_ENV || 'development';
  },

  get IS_DEV() {
    return this.MODE === 'development';
  },

  get IS_PROD() {
    return this.MODE === 'production';
  },

  get IS_TEST() {
    return this.NODE_ENV === 'test';
  },

  get I18N_DEBUG() {
    return process.env.VITE_I18N_DEBUG || 'false';
  },

  get MSW_ENABLED() {
    return process.env.VITE_MSW_ENABLED === 'true';
  },

  get DEFAULT_DATASET_KEY() {
    return process.env.VITE_DEFAULT_DATASET_KEY?.trim() || '';
  },
};
